/* ============================================================================
   CRUX STOREFRONT — INTERACTIVE LAYER (vanilla, classic script, file:// safe)
   No libraries, no ES modules, no fetch. Honors prefers-reduced-motion.
   Pairs with: components.css + ../design-tokens.css
   ----------------------------------------------------------------------------
   JS hooks (data-attributes) page builders wire to:
     data-reveal[ ="wipe"]        scroll-reveal target (cascade via --i)
     <scroll-reveal>              custom element wrapping reveal targets
     data-drawer-open="ID"        opens drawer/cart-drawer #ID
     data-drawer-close            closes the nearest open drawer/modal
     data-menu-toggle="ID"        toggles the mobile menu drawer #ID
     data-megamenu                a .c-has-megamenu trigger (hover/focus + click)
     data-modal-open="ID"         opens modal/quick-view/sizechart #ID
     data-modal-close             closes the nearest open modal
     data-gallery                 PDP gallery root
       data-gallery-thumb="N"       thumb that activates stage image N
     data-sticky-atc              sticky ATC bar (reveals at 60% scroll)
     data-accordion               accordion root (children = .c-accordion__item)
     data-tab-group / data-tab="K" / data-tab-panel="K"   content-tabs
     data-countdown="ISO"         flip-clock target datetime
     data-scratch                 scratch card root (canvas auto-built)
     data-tryon                   try-on button; data-tryon-target="SEL"
     data-freeship="MIN"          free-ship bar; data-freeship-current="N"
     data-compare                 before/after comparison slider
     data-marker / data-markers   shoppable hotspots
     data-trending                trending-search chip (fills nearest input)
     data-marquee-autofill        marquee track to clone until it overflows
     data-slideshow               full-bleed hero slideshow root (img OR video)
       data-slideshow-interval="MS"   auto-advance interval (default 7000)
       [data-slide-prev]/[data-slide-next]   prev/next controls
       data-slide-dot="i"             dot indicator for slide i
       (auto-advance pauses on hover/focus + when tab hidden; arrows + dots +
        keyboard ←/→; NO auto-advance under prefers-reduced-motion; <video> in
        the active slide plays, others pause; restart-safe)
     data-deck                    Complex-style card-deck carousel root
       [data-deck-prev]/[data-deck-next]   scroll by one card
       (.is-active on most-centered .c-deck__card, .is-peek on neighbours;
        pointer drag-to-scroll; recomputes on scroll via getBoundingClientRect)
     data-stepper                 Hostem look-stepper root
       data-step-look="i"             look figure i (one shown, neighbours ghosted)
       data-step-detail="i"           details panel synced to look i (optional)
       [data-step-prev]/[data-step-next]   move active index (wraps)
     data-storemap                STORE-LOCATOR map root (.c-storemap)
       .c-storemap__canvas          google.maps.Map mount (real map)
       .c-storemap__fallback        styled ANIMATED fallback (no key / load fail)
       [data-store-find]            "Find my closest store" button
       .c-storemap__hint            quiet line shown when geolocation denied
       [data-store-card="ID"]       store card (.c-store-card) for store ID
       .c-store-card__dist          distance slot filled after geolocation
       .sl-fb__pin[data-store-pin="ID"]   fallback SVG pin for store ID
       Config (set on window before app.js, e.g. in the page <head>/<body>):
         window.CRUX_MAPS_KEY       Google Maps JS API key ('' => fallback)
         window.CRUX_MAPS_ID        optional vector Map ID (enables 3D tilt)
         window.CRUX_STORES         [{id,name,address,hours,whatsapp,lat,lng}]
   ============================================================================ */
(function () {
  'use strict';

  /* ---- 0. js/no-js + reduced-motion ------------------------------------- */
  var docEl = document.documentElement;
  docEl.classList.remove('no-js');
  docEl.classList.add('js');

  var REDUCE = false;
  try {
    REDUCE = window.matchMedia &&
             window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { REDUCE = false; }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts || false); }

  /* ========================================================================
     1. SCROLL-REVEAL  (<scroll-reveal> custom element + [data-reveal])
        IntersectionObserver → .is-visible, staggered, then unobserve.
        Fails OPEN: no IO support → reveal everything immediately.
     ===================================================================== */
  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-visible');
  }
  function collectTargets(root) {
    var t = $all('[data-reveal]', root);
    if (root.hasAttribute && root.hasAttribute('data-reveal')) t.unshift(root);
    return t;
  }

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || docEl.clientHeight || 0;
    // already on (or near the bottom of) the first screen
    return r.top < vh * 0.95 && r.bottom > 0;
  }

  var io = null;
  if (!REDUCE && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8%' });
  }

  function bootReveal(root) {
    var targets = collectTargets(root || document);
    // assign stagger index per group of siblings if not authored
    targets.forEach(function (el) {
      if (!el.style.getPropertyValue('--i') && el.parentNode) {
        var sibs = $all('[data-reveal]', el.parentNode).filter(function (n) {
          return n.parentNode === el.parentNode;
        });
        var idx = sibs.indexOf(el);
        if (idx > 0) el.style.setProperty('--i', String(idx));
      }
    });
    if (io && !REDUCE) {
      targets.forEach(function (el) {
        // Above-the-fold content reveals IMMEDIATELY (incl. the hero clip-wipe),
        // so it is never stuck invisible if the observer doesn't fire for an
        // already-in-view element. Below-the-fold reveals on scroll.
        if (inViewport(el)) el.classList.add('is-visible');
        else io.observe(el);
      });
      // Belt-and-suspenders: anything still hidden but in view shortly after
      // load (late layout, fonts, slow paint) is force-revealed — never stuck.
      setTimeout(function () {
        targets.forEach(function (el) {
          if (el.classList.contains('is-visible')) return;
          if (inViewport(el)) { el.classList.add('is-visible'); io.unobserve(el); }
        });
      }, 1200);
    } else {
      revealAll(targets); // fail-open (no IO / reduced-motion)
    }
  }

  // Custom element (progressive — falls back to attribute bootstrap below).
  if ('customElements' in window) {
    try {
      function ScrollReveal() { return Reflect.construct(HTMLElement, [], ScrollReveal); }
      ScrollReveal.prototype = Object.create(HTMLElement.prototype);
      ScrollReveal.prototype.constructor = ScrollReveal;
      ScrollReveal.prototype.connectedCallback = function () { bootReveal(this); };
      customElements.define('scroll-reveal', ScrollReveal);
    } catch (e) { /* ignore — attribute bootstrap covers it */ }
  }

  /* ========================================================================
     2. HEADER — sticky on-scroll-up, solid-on-scroll, mobile drawer, mega-menu
     ===================================================================== */
  function initHeader() {
    var header = $('.c-header');
    if (!header) return;
    var lastY = window.pageYOffset || 0;
    var SOLID_AT = 8;

    function onScroll() {
      var y = window.pageYOffset || docEl.scrollTop || 0;
      if (y > SOLID_AT) header.classList.add('is-solid');
      else header.classList.remove('is-solid');
      // on-scroll-up: hide when scrolling down past the header height
      if (y > lastY && y > header.offsetHeight) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
      lastY = y;
    }
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();

    // mega-menu: hover handled in CSS; add keyboard + click toggle.
    $all('[data-megamenu]').forEach(function (trigger) {
      var menu = $('.c-megamenu', trigger.closest('.c-has-megamenu') || document);
      on(trigger, 'click', function (e) {
        if (!menu) return;
        e.preventDefault();
        menu.classList.toggle('is-open');
      });
      on(trigger, 'keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && menu) {
          e.preventDefault();
          menu.classList.toggle('is-open');
        }
      });
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape') {
        $all('.c-megamenu.is-open').forEach(function (m) { m.classList.remove('is-open'); });
      }
    });
  }

  /* ========================================================================
     3. DRAWERS (menu / cart) + MODALS (quick-view / size-chart) + focus trap
     ===================================================================== */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
                  'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var lastFocus = null;

  function trapFocus(container, e) {
    var nodes = $all(FOCUSABLE, container).filter(function (n) { return n.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openOverlay(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    docEl.style.overflow = 'hidden';
    var focusTarget = $('[data-autofocus]', el) || $(FOCUSABLE, el);
    if (focusTarget) setTimeout(function () { focusTarget.focus(); }, REDUCE ? 0 : 60);
  }
  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (!$('.c-drawer.is-open, .c-modal.is-open')) docEl.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function initOverlays() {
    // openers
    $all('[data-drawer-open], [data-menu-toggle], [data-modal-open]').forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-drawer-open') ||
                 btn.getAttribute('data-menu-toggle') ||
                 btn.getAttribute('data-modal-open');
        openOverlay(document.getElementById(id));
      });
    });
    // closers — buttons + scrim
    on(document, 'click', function (e) {
      var closer = e.target.closest('[data-drawer-close], [data-modal-close]');
      if (closer) {
        e.preventDefault();
        closeOverlay(closer.closest('.c-drawer, .c-modal'));
        return;
      }
      if (e.target.classList && e.target.classList.contains('c-drawer__scrim')) {
        closeOverlay(e.target.closest('.c-drawer'));
      }
      if (e.target.classList && e.target.classList.contains('c-modal__scrim')) {
        closeOverlay(e.target.closest('.c-modal'));
      }
    });
    // ESC + focus trap
    on(document, 'keydown', function (e) {
      var open = $('.c-modal.is-open') || $('.c-drawer.is-open');
      if (!open) return;
      if (e.key === 'Escape') closeOverlay(open);
      else if (e.key === 'Tab') trapFocus(open, e);
    });
  }

  /* ========================================================================
     4. PDP GALLERY — thumb click cross-fades the stage image
     ===================================================================== */
  function initGallery() {
    $all('[data-gallery]').forEach(function (root) {
      var stages = $all('.c-pdp-gallery__stage img', root);
      var thumbs = $all('[data-gallery-thumb]', root);
      function activate(n) {
        stages.forEach(function (img, i) { img.classList.toggle('is-active', i === n); });
        thumbs.forEach(function (t, i) {
          var k = parseInt(t.getAttribute('data-gallery-thumb'), 10);
          t.classList.toggle('is-active', k === n);
          t.setAttribute('aria-current', k === n ? 'true' : 'false');
        });
      }
      thumbs.forEach(function (t) {
        on(t, 'click', function () { activate(parseInt(t.getAttribute('data-gallery-thumb'), 10)); });
      });
      if (stages.length && !$('.c-pdp-gallery__stage img.is-active', root)) activate(0);
    });
  }

  /* ========================================================================
     5. STICKY ATC — reveals at 60% page scroll (mobile)
     ===================================================================== */
  function initStickyAtc() {
    $all('[data-sticky-atc]').forEach(function (bar) {
      function check() {
        var max = (document.body.scrollHeight - window.innerHeight) || 1;
        var pct = (window.pageYOffset || 0) / max;
        bar.classList.toggle('is-visible', pct >= 0.6);
      }
      on(window, 'scroll', check, { passive: true });
      check();
    });
  }

  /* ========================================================================
     6. SIZE-CHART — handled by generic modal openers (data-modal-open).
        Accordion (collapsible / content-tabs) slideToggle via height.
     ===================================================================== */
  function setPanelHeight(panel, open) {
    if (REDUCE) { panel.style.height = open ? 'auto' : '0px'; return; }
    if (open) {
      panel.style.height = panel.scrollHeight + 'px';
      var done = function () {
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', done);
      };
      panel.addEventListener('transitionend', done);
    } else {
      panel.style.height = panel.scrollHeight + 'px';
      // force reflow so the transition runs from explicit px → 0
      void panel.offsetHeight;
      panel.style.height = '0px';
    }
  }

  function initAccordion() {
    $all('[data-accordion]').forEach(function (root) {
      var single = root.hasAttribute('data-accordion-single');
      $all('.c-accordion__item', root).forEach(function (item) {
        var trigger = $('.c-accordion__trigger', item);
        var panel = $('.c-accordion__panel', item);
        if (!trigger || !panel) return;
        var openInit = item.classList.contains('is-open');
        panel.style.height = openInit ? 'auto' : '0px';
        trigger.setAttribute('aria-expanded', openInit ? 'true' : 'false');
        on(trigger, 'click', function () {
          var willOpen = !item.classList.contains('is-open');
          if (single && willOpen) {
            $all('.c-accordion__item.is-open', root).forEach(function (other) {
              if (other !== item) {
                other.classList.remove('is-open');
                var op = $('.c-accordion__panel', other);
                var ot = $('.c-accordion__trigger', other);
                if (op) setPanelHeight(op, false);
                if (ot) ot.setAttribute('aria-expanded', 'false');
              }
            });
          }
          item.classList.toggle('is-open', willOpen);
          trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          setPanelHeight(panel, willOpen);
        });
      });
    });
  }

  /* ========================================================================
     7. CONTENT-TABS (data-tab-group)
     ===================================================================== */
  function initTabs() {
    $all('[data-tab-group]').forEach(function (group) {
      var tabs = $all('[data-tab]', group);
      var panels = $all('[data-tab-panel]', group);
      function activate(key) {
        tabs.forEach(function (t) {
          var is = t.getAttribute('data-tab') === key;
          t.classList.toggle('is-active', is);
          t.setAttribute('aria-selected', is ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          p.hidden = p.getAttribute('data-tab-panel') !== key;
        });
      }
      tabs.forEach(function (t) {
        on(t, 'click', function () { activate(t.getAttribute('data-tab')); });
      });
      var active = $('[data-tab].is-active', group) || tabs[0];
      if (active) activate(active.getAttribute('data-tab'));
    });
  }

  /* ========================================================================
     8. COUNTDOWN flip-clock (data-countdown="ISO" or near-future demo target)
     ===================================================================== */
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function initCountdown() {
    $all('[data-countdown]').forEach(function (root) {
      var iso = root.getAttribute('data-countdown');
      var target;
      if (iso) target = new Date(iso).getTime();
      if (!target || isNaN(target)) {
        // HONESTY (vet 2026-07-02): no/invalid datetime → hide the clock,
        // never invent a fake countdown.
        root.hidden = true;
        var box = root.closest('[data-countdown-wrap]');
        if (box) box.hidden = true;
        return;
      }

      var units = ['days', 'hrs', 'mins', 'secs'];
      var slots = {};
      units.forEach(function (u) {
        slots[u] = $all('.cx-countdown__digit[data-unit="' + u + '"]', root);
      });
      var prev = {};

      function setUnit(unit, value) {
        var str = pad2(value);
        var digits = slots[unit];
        if (!digits || !digits.length) return;
        // map each character to a digit slot (last N slots)
        var offset = digits.length - str.length;
        for (var i = 0; i < digits.length; i++) {
          var ch = i - offset >= 0 ? str.charAt(i - offset) : '0';
          if (digits[i].textContent !== ch) {
            digits[i].textContent = ch;
            if (!REDUCE) {
              digits[i].classList.remove('is-flipping');
              void digits[i].offsetWidth; // restart animation
              digits[i].classList.add('is-flipping');
            }
          }
        }
      }

      function tick() {
        var diff = target - Date.now();
        if (diff < 0) {
          diff = 0;
          root.classList.add('is-elapsed');
          // unlock any gated drop cards / ATC tied to this clock
          $all('.cx-drop-card.is-locked').forEach(function (c) {
            c.classList.remove('is-locked'); c.classList.add('is-unlocked');
          });
        }
        var s = Math.floor(diff / 1000);
        var vals = {
          days: Math.floor(s / 86400),
          hrs: Math.floor((s % 86400) / 3600),
          mins: Math.floor((s % 3600) / 60),
          secs: s % 60
        };
        units.forEach(function (u) {
          if (prev[u] !== vals[u]) { setUnit(u, vals[u]); prev[u] = vals[u]; }
        });
        if (diff > 0) setTimeout(tick, 1000);
      }
      tick();
    });
  }

  /* ========================================================================
     9. SCRATCH card (pointer/touch scratch-to-reveal on canvas)
     ===================================================================== */
  function initScratch() {
    $all('[data-scratch]').forEach(function (root) {
      var canvas = $('.cx-scratch__canvas', root);
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = 'cx-scratch__canvas';
        root.appendChild(canvas);
      }
      var ctx = canvas.getContext && canvas.getContext('2d');
      if (!ctx) { root.classList.add('is-revealed'); return; } // fail-open

      function reveal() {
        if (root.classList.contains('is-revealed')) return;
        root.classList.add('is-revealed');
      }
      if (REDUCE) { reveal(); return; }

      function sizeCanvas() {
        var r = root.getBoundingClientRect();
        canvas.width = Math.max(1, Math.round(r.width));
        canvas.height = Math.max(1, Math.round(r.height));
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = '#9A9A9A';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#F5F5F2';
        ctx.font = '600 13px Archivo, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('SCRATCH TO REVEAL', canvas.width / 2, canvas.height / 2);
        ctx.globalCompositeOperation = 'destination-out';
      }
      sizeCanvas();

      var drawing = false;
      function pos(e) {
        var r = canvas.getBoundingClientRect();
        var p = (e.touches && e.touches[0]) || e;
        return { x: p.clientX - r.left, y: p.clientY - r.top };
      }
      function scratch(e) {
        if (!drawing) return;
        var p = pos(e);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 22, 0, Math.PI * 2);
        ctx.fill();
        if (e.cancelable) e.preventDefault();
      }
      function cleared() {
        try {
          var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          var clear = 0;
          for (var i = 3; i < data.length; i += 40) { if (data[i] === 0) clear++; }
          return clear / (data.length / 40) > 0.5;
        } catch (err) { return false; }
      }
      function end() {
        drawing = false;
        if (cleared()) reveal();
      }
      on(canvas, 'mousedown', function (e) { drawing = true; scratch(e); });
      on(canvas, 'mousemove', scratch);
      on(window, 'mouseup', end);
      on(canvas, 'touchstart', function (e) { drawing = true; scratch(e); }, { passive: false });
      on(canvas, 'touchmove', scratch, { passive: false });
      on(canvas, 'touchend', end);
      on(window, 'resize', sizeCanvas);
    });
  }

  /* ========================================================================
     10. TRY-ON button — simulate render then run one-time shimmer sweep
     ===================================================================== */
  function initTryon() {
    $all('[data-tryon]').forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        if (btn.classList.contains('is-loading')) return;
        var sel = btn.getAttribute('data-tryon-target');
        var target = sel ? $(sel) : null;
        btn.classList.add('is-loading');
        var label = $('.cx-tryon-btn__label', btn) || btn;
        var prevText = label.textContent;
        var delay = REDUCE ? 200 : 1100;
        setTimeout(function () {
          btn.classList.remove('is-loading');
          label.textContent = prevText;
          if (target) {
            target.classList.add('cx-tryon-target');
            if (!REDUCE) {
              target.classList.remove('is-shimmering');
              void target.offsetWidth;
              target.classList.add('is-shimmering');
              setTimeout(function () { target.classList.remove('is-shimmering'); }, 400);
            }
          }
        }, delay);
      });
    });
  }

  /* ========================================================================
     11. FREE-SHIP bar (demo fill)
     ===================================================================== */
  function initFreeship() {
    $all('[data-freeship]').forEach(function (bar) {
      var min = parseFloat(bar.getAttribute('data-freeship')) || 150000;
      var cur = parseFloat(bar.getAttribute('data-freeship-current')) || 0;
      var fill = $('.c-freeship-bar__fill', bar);
      var label = $('.c-freeship-bar__label', bar);
      var pct = Math.min(100, (cur / min) * 100);
      if (fill) requestAnimationFrame(function () { fill.style.width = pct + '%'; });
      if (cur >= min) {
        bar.classList.add('is-unlocked');
        if (label) label.textContent = 'Free Lagos delivery unlocked';
      } else if (label) {
        var rem = Math.round(min - cur).toLocaleString('en-NG');
        label.textContent = 'NGN ' + rem + ' to free Lagos delivery';
      }
    });
  }

  /* ========================================================================
     12. COMPARE slider (factory → finished)
     ===================================================================== */
  function initCompare() {
    $all('[data-compare]').forEach(function (root) {
      var after = $('.c-compare__after', root);
      var handle = $('.c-compare__handle', root);
      if (!after || !handle) return;
      var dragging = false;
      function setPos(clientX) {
        var r = root.getBoundingClientRect();
        var pct = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
        after.style.clipPath = 'inset(0 0 0 ' + pct + '%)';
        handle.style.left = pct + '%';
      }
      function move(e) {
        if (!dragging) return;
        var p = (e.touches && e.touches[0]) || e;
        setPos(p.clientX);
        if (e.cancelable) e.preventDefault();
      }
      on(handle, 'mousedown', function () { dragging = true; });
      on(handle, 'touchstart', function () { dragging = true; }, { passive: true });
      on(window, 'mousemove', move);
      on(window, 'touchmove', move, { passive: false });
      on(window, 'mouseup', function () { dragging = false; });
      on(window, 'touchend', function () { dragging = false; });
      on(root, 'click', function (e) {
        var p = (e.touches && e.touches[0]) || e;
        setPos(p.clientX);
      });
    });
  }

  /* ========================================================================
     13. MARKERS (shoppable hotspots) — tap dot reveals product card
     ===================================================================== */
  function initMarkers() {
    $all('[data-markers]').forEach(function (root) {
      var dots = $all('[data-marker]', root);
      dots.forEach(function (dot) {
        on(dot, 'click', function (e) {
          e.stopPropagation();
          var wasActive = dot.classList.contains('is-active');
          dots.forEach(function (d) { d.classList.remove('is-active'); });
          if (!wasActive) dot.classList.add('is-active');
        });
      });
      on(document, 'click', function () {
        dots.forEach(function (d) { d.classList.remove('is-active'); });
      });
    });
  }

  /* ========================================================================
     14. TRENDING-SEARCH chips → fill the nearest search input
     ===================================================================== */
  function initTrending() {
    $all('[data-trending]').forEach(function (chip) {
      on(chip, 'click', function () {
        var term = chip.getAttribute('data-trending') || chip.textContent.trim();
        var input = $('[data-search-input]') || $('input[type="search"]') || $('.c-input');
        if (input) { input.value = term; input.focus(); input.dispatchEvent(new Event('input', { bubbles: true })); }
      });
    });
  }

  /* ========================================================================
     15. MARQUEE auto-fill — clone the track until it overflows (seamless loop)
     ===================================================================== */
  function initMarquee() {
    $all('[data-marquee-autofill]').forEach(function (track) {
      var firstSet = track.innerHTML;
      var guard = 0;
      while (track.scrollWidth < track.parentNode.offsetWidth * 2 && guard < 8) {
        track.insertAdjacentHTML('beforeend', firstSet);
        guard++;
      }
      // duplicate once more so the -50% translate loops seamlessly
      track.insertAdjacentHTML('beforeend', track.innerHTML);
    });
  }

  /* ========================================================================
     16. SLIDESHOW (full-bleed hero, image OR video slides, cross-fade)
         Auto-advance (paused under reduced-motion / hover / focus / hidden tab),
         prev/next, dots, keyboard ←/→. Active <video> plays; others pause.
     ===================================================================== */
  function initSlideshow() {
    $all('[data-slideshow]').forEach(function (root) {
      var slides = $all('.c-slideshow__slide', root);
      if (!slides.length) return;
      var dots = $all('[data-slide-dot]', root);
      var interval = parseInt(root.getAttribute('data-slideshow-interval'), 10) || 7000;
      var idx = 0;
      var timer = null;
      var paused = false;

      // pick the initially-active slide if authored, else 0
      slides.forEach(function (s, i) { if (s.classList.contains('is-active')) idx = i; });

      function syncVideo(slide, play) {
        var vid = slide.querySelector('video');
        if (!vid) return;
        try {
          if (play) { vid.muted = true; var p = vid.play(); if (p && p.catch) p.catch(function () {}); }
          else { vid.pause(); }
        } catch (e) { /* file:// / autoplay-blocked — poster stays visible */ }
      }

      function show(n) {
        idx = (n + slides.length) % slides.length;
        slides.forEach(function (s, i) {
          var on = i === idx;
          s.classList.toggle('is-active', on);
          syncVideo(s, on);
        });
        dots.forEach(function (d, i) {
          var k = parseInt(d.getAttribute('data-slide-dot'), 10);
          var on = k === idx;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }
      function next() { show(idx + 1); }
      function prev() { show(idx - 1); }

      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      function start() {
        stop();
        if (REDUCE || paused || slides.length < 2) return;   // manual only under reduced-motion
        timer = setInterval(next, interval);
      }
      function restart() { start(); }   // restart-safe: any interaction re-bases the clock

      $all('[data-slide-next]', root).forEach(function (b) {
        on(b, 'click', function () { next(); restart(); });
      });
      $all('[data-slide-prev]', root).forEach(function (b) {
        on(b, 'click', function () { prev(); restart(); });
      });
      dots.forEach(function (d) {
        on(d, 'click', function () { show(parseInt(d.getAttribute('data-slide-dot'), 10) || 0); restart(); });
      });

      // pause on hover / focus-within
      on(root, 'mouseenter', function () { paused = true; stop(); });
      on(root, 'mouseleave', function () { paused = false; start(); });
      on(root, 'focusin', function () { paused = true; stop(); });
      on(root, 'focusout', function () {
        if (!root.contains(document.activeElement)) { paused = false; start(); }
      });
      // pause when tab hidden (saves video decode + avoids surprise jumps)
      on(document, 'visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });
      // keyboard arrows when focus is inside the slideshow
      on(root, 'keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); next(); restart(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); restart(); }
      });

      show(idx);
      start();
    });
  }

  /* ========================================================================
     17. DECK (Complex-style card-deck) — scroll-snap track, center-active,
         neighbours peek (scale + dim), arrow nav, pointer drag-to-scroll.
     ===================================================================== */
  function initDeck() {
    $all('[data-deck]').forEach(function (root) {
      var track = $('.c-deck__track', root);
      if (!track) return;
      var cards = $all('.c-deck__card', track);
      if (!cards.length) return;

      // mark the most-centered card .is-active, immediate neighbours .is-peek
      var raf = null;
      function update() {
        raf = null;
        var tr = track.getBoundingClientRect();
        var center = tr.left + tr.width / 2;
        var best = 0, bestDist = Infinity;
        cards.forEach(function (c, i) {
          var r = c.getBoundingClientRect();
          var cc = r.left + r.width / 2;
          var d = Math.abs(cc - center);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        cards.forEach(function (c, i) {
          c.classList.toggle('is-active', i === best);
          c.classList.toggle('is-peek', i === best - 1 || i === best + 1);
        });
      }
      function schedule() { if (!raf) raf = requestAnimationFrame(update); }
      on(track, 'scroll', schedule, { passive: true });
      on(window, 'resize', schedule);

      function scrollByCard(dir) {
        var step = cards[0] ? cards[0].getBoundingClientRect().width + 24 : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * step, behavior: REDUCE ? 'auto' : 'smooth' });
      }
      $all('[data-deck-next]', root).forEach(function (b) { on(b, 'click', function () { scrollByCard(1); }); });
      $all('[data-deck-prev]', root).forEach(function (b) { on(b, 'click', function () { scrollByCard(-1); }); });

      // pointer drag-to-scroll (mouse; touch uses native scroll)
      var down = false, startX = 0, startLeft = 0, moved = false;
      on(track, 'mousedown', function (e) {
        down = true; moved = false;
        startX = e.clientX; startLeft = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      on(window, 'mousemove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        track.scrollLeft = startLeft - dx;
      });
      on(window, 'mouseup', function () {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
        schedule();
      });
      // suppress click on cards after a drag (so drag doesn't fire add/wish)
      on(track, 'click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);

      update();
    });
  }

  /* ========================================================================
     18. STEPPER (Hostem look-stepper) — one look active, neighbours ghosted,
         arrows advance/wrap, details column synced to the active look.
     ===================================================================== */
  function initStepper() {
    $all('[data-stepper]').forEach(function (root) {
      var looks = $all('[data-step-look]', root);
      if (!looks.length) return;
      var details = $all('[data-step-detail]', root);
      var idx = 0;
      looks.forEach(function (l, i) { if (l.classList.contains('is-active')) idx = i; });

      function show(n) {
        idx = (n + looks.length) % looks.length;
        var prevI = (idx - 1 + looks.length) % looks.length;
        var nextI = (idx + 1) % looks.length;
        looks.forEach(function (l, i) {
          l.classList.toggle('is-active', i === idx);
          // only flag neighbours when there are >2 looks (avoid same-node prev+next)
          l.classList.toggle('is-prev', looks.length > 2 && i === prevI);
          l.classList.toggle('is-next', looks.length > 2 && i === nextI);
        });
        details.forEach(function (d) {
          d.classList.toggle('is-active', parseInt(d.getAttribute('data-step-detail'), 10) === idx);
        });
      }
      $all('[data-step-next]', root).forEach(function (b) { on(b, 'click', function () { show(idx + 1); }); });
      $all('[data-step-prev]', root).forEach(function (b) { on(b, 'click', function () { show(idx - 1); }); });
      on(root, 'keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); show(idx - 1); }
      });

      show(idx);
    });
  }

  /* ========================================================================
     19. STORE-MAP (the animated hero) — real Google Map when keyed, else a
         styled animated fallback. Geolocation -> nearest store + distances +
         directions. Pin <-> card sync. All vanilla, file:// safe, RM-aware.
     ===================================================================== */
  function initStoreMap() {
    var root = $('[data-storemap]');
    if (!root) return;

    var KEY    = window.CRUX_MAPS_KEY || '';
    var MAP_ID = window.CRUX_MAPS_ID || '';
    var stores = (window.CRUX_STORES && window.CRUX_STORES.length) ? window.CRUX_STORES : [];
    if (!stores.length) return;

    // root = [data-storemap] wraps BOTH columns (so card<->pin sync reaches the
    // list). mapShell = the actual map box that carries .is-fallback/.is-map-ready.
    var mapShell = $('.c-storemap', root) || root;
    var canvas   = $('.c-storemap__canvas', root);
    var findBtn  = $('[data-store-find]', root);
    var hint     = $('.c-storemap__hint', root);
    var fbPins   = $all('.sl-fb__pin', root);

    var map = null;             // google.maps.Map (when keyed)
    var markers = {};           // storeId -> marker
    var gready = false;         // google map confirmed alive
    var loadTimer = null;

    function cardFor(id) { return $('[data-store-card="' + id + '"]'); }
    function pinFor(id)  { return root.querySelector('.sl-fb__pin[data-store-pin="' + id + '"]'); }

    function showHint(msg) { if (hint) { hint.textContent = msg; hint.hidden = false; } }
    function hideHint()    { if (hint) hint.hidden = true; }

    /* ---- haversine (km) ---- */
    function distKm(aLat, aLng, bLat, bLng) {
      var R = 6371, toRad = Math.PI / 180;
      var dLat = (bLat - aLat) * toRad, dLng = (bLng - aLng) * toRad;
      var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(aLat * toRad) * Math.cos(bLat * toRad) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
      return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
    }
    function fmtKm(km) {
      if (km < 1) return Math.round(km * 1000) + ' m';
      return (km < 10 ? km.toFixed(1) : Math.round(km)) + ' km';
    }

    /* ---- pin <-> card sync (works for fallback SVG pins) ---- */
    var flashTimer = null;
    function selectStore(id) {
      var card = cardFor(id);
      fbPins.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-store-pin') === id); });
      if (!card) return;
      card.scrollIntoView({ behavior: REDUCE ? 'auto' : 'smooth', block: 'center' });
      $all('.c-store-card.is-flash').forEach(function (c) { c.classList.remove('is-flash'); });
      if (flashTimer) clearTimeout(flashTimer);
      void card.offsetWidth;
      card.classList.add('is-flash');
      flashTimer = setTimeout(function () { card.classList.remove('is-flash'); }, 1400);
      if (map && markers[id]) {
        try {
          map.panTo({ lat: stores.filter(function (s) { return s.id === id; })[0].lat,
                      lng: stores.filter(function (s) { return s.id === id; })[0].lng });
        } catch (e) {}
      }
    }

    fbPins.forEach(function (pin) {
      var id = pin.getAttribute('data-store-pin');
      on(pin, 'click', function () { selectStore(id); });
      on(pin, 'keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); selectStore(id); }
      });
    });
    // hovering a card lifts its matching fallback pin
    $all('[data-store-card]').forEach(function (card) {
      var id = card.getAttribute('data-store-card');
      var pin = pinFor(id);
      if (pin) {
        on(card, 'mouseenter', function () { pin.classList.add('is-lifted'); });
        on(card, 'mouseleave', function () { pin.classList.remove('is-lifted'); });
      }
      // click a marker on the real map -> select; clicking card name handled by anchor.
    });

    /* ---- nearest-store: fill distances, mark closest, focus map ---- */
    function applyNearest(userLat, userLng) {
      var ranked = stores.map(function (s) {
        return { id: s.id, km: distKm(userLat, userLng, s.lat, s.lng) };
      }).sort(function (a, b) { return a.km - b.km; });

      var closestId = ranked[0] && ranked[0].id;
      ranked.forEach(function (r) {
        var card = cardFor(r.id);
        if (card) {
          var slot = $('.c-store-card__dist', card);
          if (slot) slot.textContent = fmtKm(r.km) + ' away';
          card.classList.toggle('is-closest', r.id === closestId);
        }
        var pin = pinFor(r.id);
        if (pin) pin.classList.toggle('is-closest', r.id === closestId);
      });

      // focus the map on the nearest (real map: fitBounds user+store; bounce marker)
      if (map && gready && window.google && closestId) {
        try {
          var cs = stores.filter(function (s) { return s.id === closestId; })[0];
          var bounds = new google.maps.LatLngBounds();
          bounds.extend({ lat: userLat, lng: userLng });
          bounds.extend({ lat: cs.lat, lng: cs.lng });
          map.fitBounds(bounds, 80);
          bounceMarker(closestId);
        } catch (e) {}
      } else if (closestId) {
        selectStore(closestId);   // fallback: emphasize the nearest pin + scroll card
      }
      return closestId;
    }

    function bounceMarker(id) {
      var m = markers[id];
      if (!m || REDUCE) return;
      try {
        if (m.setAnimation && window.google && google.maps.Animation) {
          m.setAnimation(google.maps.Animation.BOUNCE);
          setTimeout(function () { try { m.setAnimation(null); } catch (e) {} }, 1500);
        }
      } catch (e) {}
    }

    function locate(loud) {
      if (!navigator.geolocation) {
        if (loud) showHint('Location unavailable — pick a store below');
        return;
      }
      if (findBtn && loud) { findBtn.classList.add('is-disabled'); }
      navigator.geolocation.getCurrentPosition(function (pos) {
        if (findBtn) findBtn.classList.remove('is-disabled');
        hideHint();
        applyNearest(pos.coords.latitude, pos.coords.longitude);
      }, function () {
        if (findBtn) findBtn.classList.remove('is-disabled');
        // quiet, never shout: show all stores + a calm allow-location line
        showHint('Allow location to find your closest store');
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    }

    if (findBtn) on(findBtn, 'click', function () { locate(true); });

    /* ---- fallback path: reveal the animated SVG map ---- */
    function showFallback() {
      mapShell.classList.add('is-fallback');
      mapShell.classList.remove('is-map-ready');
    }

    /* ---- real Google Map path ---- */
    function buildMap() {
      if (!window.google || !google.maps) { showFallback(); return; }
      try {
        var center = stores[0];
        var opts = {
          center: { lat: center.lat, lng: center.lng },
          zoom: 11,
          disableDefaultUI: false,
          clickableIcons: false,
          gestureHandling: 'greedy'
        };
        if (MAP_ID) opts.mapId = MAP_ID;            // vector -> enables tilt/3D buildings
        map = new google.maps.Map(canvas, opts);

        // markers — AdvancedMarkerElement when available, else legacy Marker
        var hasAdv = google.maps.marker && google.maps.marker.AdvancedMarkerElement;
        stores.forEach(function (s) {
          var mk;
          if (hasAdv) {
            mk = new google.maps.marker.AdvancedMarkerElement({
              map: map, position: { lat: s.lat, lng: s.lng }, title: s.name
            });
            mk.addListener('click', function () { selectStore(s.id); });
          } else {
            mk = new google.maps.Marker({
              map: map, position: { lat: s.lat, lng: s.lng }, title: s.name
            });
            mk.addListener('click', function () { selectStore(s.id); });
          }
          markers[s.id] = mk;
        });

        google.maps.event.addListenerOnce(map, 'idle', function () {
          gready = true;
          if (loadTimer) { clearTimeout(loadTimer); loadTimer = null; }
          mapShell.classList.remove('is-fallback');
          mapShell.classList.add('is-map-ready');

          // 3D effect: tilt + gentle heading ease on load (vector Map ID only).
          if (MAP_ID && !REDUCE) {
            try {
              map.setTilt(47.5);
              var h0 = 0, hTarget = 30, t0 = null, DUR = 1500;
              var step = function (ts) {
                if (t0 === null) t0 = ts;
                var k = Math.min(1, (ts - t0) / DUR);
                var eased = 1 - Math.pow(1 - k, 3);   // ease-out cubic
                map.setHeading(h0 + (hTarget - h0) * eased);
                if (k < 1) requestAnimationFrame(step);
              };
              requestAnimationFrame(step);
            } catch (e) { /* raster / no-WebGL: Google auto-degrades to flat 2D */ }
          }

          locate(false);   // soft on-load attempt (no shouting if denied)
        });
      } catch (e) {
        showFallback();
      }
    }

    if (KEY) {
      // global callback Google invokes when the JS API finishes loading
      window.__cruxMapInit = function () { buildMap(); };
      // guard: if google never loads / throws, drop to the animated fallback
      loadTimer = setTimeout(function () { if (!gready) showFallback(); }, 6000);

      var src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(KEY) +
                '&v=weekly&libraries=marker&callback=__cruxMapInit&loading=async';
      var sc = document.createElement('script');
      sc.src = src; sc.async = true; sc.defer = true;
      sc.onerror = function () { showFallback(); };
      // Google's gm_authFailure fires on a bad/unauthorized key -> fallback
      window.gm_authFailure = function () { showFallback(); };
      document.head.appendChild(sc);
    } else {
      // no key: animated fallback immediately, but still offer nearest-store math
      showFallback();
      locate(false);
    }
  }

  /* ========================================================================
     INIT
     ===================================================================== */
  ready(function () {
    bootReveal(document);   // attribute bootstrap (covers non-custom-element pages)
    initHeader();
    initOverlays();
    initGallery();
    initStickyAtc();
    initAccordion();
    initTabs();
    initCountdown();
    initScratch();
    initTryon();
    initFreeship();
    initCompare();
    initMarkers();
    initTrending();
    initMarquee();
    initSlideshow();
    initDeck();
    initStepper();
    initStoreMap();
  });
})();
